/**
 * Equipment Row Utilities
 *
 * CRITICAL: Centralized ID generation to prevent data loss (GPT Guardrail #5)
 * These functions define the deterministic ID format that MUST NEVER CHANGE
 *
 * ID Format: reg:{vesselId}:{domain}:{type}:{location}:{variant}
 *
 * @module equipmentRow
 */

const toKey = (s) => String(s || '').toLowerCase().trim();

/**
 * Create deterministic equipment ID
 *
 * CRITICAL: This ID format is the linchpin for data persistence
 * Changing this format will cause data loss!
 *
 * @param {Object} params
 * @param {string} params.vesselId - Vessel identifier
 * @param {string} params.domain - Equipment domain (FFE, LSA, Navigation, etc.)
 * @param {string} params.type - Equipment type (extinguisher, lifebuoy, radar, etc.)
 * @param {string} params.location - Physical location on vessel
 * @param {string} [params.variant='base'] - Equipment variant
 * @returns {string} Deterministic equipment ID
 *
 * @example
 * createEquipmentId({
 *   vesselId: 'VSL001-25',
 *   domain: 'FFE',
 *   type: 'extinguisher',
 *   location: 'galley',
 *   variant: '5a34b'
 * })
 * // Returns: 'reg:VSL001-25:ffe:extinguisher:galley:5a34b'
 */
export function createEquipmentId({ vesselId, domain, type, location, variant = 'base' }) {
  if (!vesselId || !domain || !type) {
    throw new Error('createEquipmentId requires vesselId, domain, and type');
  }

  const loc = toKey(location || 'general');
  const var_ = toKey(variant || 'base');

  return `reg:${vesselId}:${toKey(domain)}:${toKey(type)}:${loc}:${var_}`;
}

/**
 * Create standard equipment row with locked schema
 *
 * @param {Object} params - Equipment row configuration
 * @param {string} params.id - Equipment ID (use createEquipmentId)
 * @param {string} params.category - Equipment category (FFE, LSA, etc.)
 * @param {string} params.type - Equipment type
 * @param {string} [params.location] - Physical location
 * @param {string} [params.variant='base'] - Equipment variant
 * @param {string} [params.source='requirements'] - Source: 'requirements' | 'added'
 * @param {Array<string>} [params.sourceRequirementIds] - Requirement IDs that generated this
 * @param {boolean} [params.archived=false] - Archived status
 * @param {string} [params.label] - Display label
 * @param {Object} [params.meta={}] - Additional metadata
 * @param {string} [params.guidanceKey] - Link to guidance system
 * @returns {Object} Standard equipment row
 */
export function createEquipmentRow(params) {
  const {
    id,
    category,
    type,
    location,
    variant = 'base',
    source = 'requirements',
    sourceRequirementIds = [],
    archived = false,
    label,
    meta = {},
    guidanceKey = null,
    ...rest
  } = params;

  if (!id || !category || !type) {
    throw new Error('createEquipmentRow requires id, category, and type');
  }

  // Locked schema - these are the ONLY fields equipment rows should have
  return {
    id,
    category,
    type,
    location: location || 'general',
    variant,
    source,
    sourceRequirementIds,
    archived,
    label: label || `${type} - ${location || 'general'}`,
    meta,
    guidanceKey,
    // Pass through any additional fields (for backward compatibility)
    ...rest
  };
}

/**
 * Extract location from equipment label if location field missing
 * Useful for migration and backward compatibility
 *
 * @param {string} label - Equipment label
 * @returns {string|null} Inferred location or null
 */
export function inferLocationFromLabel(label) {
  if (!label) return null;
  const lower = toKey(label);

  if (lower.includes('galley') || lower.includes('kitchen')) return 'galley';
  if (lower.includes('engine') || lower.includes('machinery')) return 'engine';
  if (lower.includes('accommodation') || lower.includes('cabin')) return 'accommodation';
  if (lower.includes('bridge') || lower.includes('wheelhouse')) return 'wheelhouse';
  if (lower.includes('deck')) return 'deck';
  if (lower.includes('battery')) return 'battery';
  if (lower.includes('electrical')) return 'electrical';

  return null;
}

// ============================================================================
// FT-189: DISPLAY NAME HELPERS - Centralized equipment name resolution
// ============================================================================

/**
 * FT-189: Get equipment display name from an equipment object
 * Canonical helper for consistent name resolution across the app.
 * Priority: name > label > item_name > type > 'Unknown Equipment'
 *
 * @param {Object|string} equipment - Equipment record object or type string
 * @returns {string} Display name for the equipment
 */
export function getEquipmentDisplayName(equipment) {
  // Handle null/undefined
  if (!equipment) return 'Unknown Equipment';

  // If it's already a string, return as-is (already a name/type)
  if (typeof equipment === 'string') {
    return equipment.trim() || 'Unknown Equipment';
  }

  // Priority chain for equipment record objects: name > label > item_name > type
  const displayName = (
    equipment.name ||
    equipment.label ||
    equipment.item_name ||
    equipment.type ||
    ''
  ).toString().trim();

  return displayName || 'Unknown Equipment';
}

/**
 * FT-189: Resolve equipment name from various input types
 * Handles: equipment objects, type strings, or equipment IDs
 *
 * @param {Object|string} equipmentInput - Equipment object, type string, or ID
 * @param {Array} equipmentList - Optional list of equipment for ID lookup
 * @returns {string} Resolved display name
 */
export function resolveEquipmentName(equipmentInput, equipmentList = []) {
  // Handle null/undefined
  if (!equipmentInput) return 'Unknown Equipment';

  // If it's an object, use getEquipmentDisplayName
  if (typeof equipmentInput === 'object') {
    return getEquipmentDisplayName(equipmentInput);
  }

  // If it's a string
  if (typeof equipmentInput === 'string') {
    const trimmed = equipmentInput.trim();

    // If it looks like an ID (has "reg:" prefix or UUID pattern), try to find in list
    if (trimmed.startsWith('reg:') || trimmed.includes('-')) {
      const found = equipmentList.find(e => e.id === trimmed);
      if (found) {
        return getEquipmentDisplayName(found);
      }
    }

    // Otherwise return as-is (it's a type or name string)
    return trimmed || 'Unknown Equipment';
  }

  return 'Unknown Equipment';
}

/**
 * FT-189: Get canonical name field for equipment storage
 * Used when normalizing equipment records - ensures `name` is always set
 *
 * @param {Object} equipment - Equipment object with various name fields
 * @returns {string} The canonical name to store
 */
export function getCanonicalEquipmentName(equipment) {
  if (!equipment) return '';

  // Priority: name > label > item_name > type
  return (
    equipment.name ||
    equipment.label ||
    equipment.item_name ||
    equipment.type ||
    ''
  ).toString().trim();
}
